/**
 * Racing a friend: a quarter mile, side by side, from a standing start. One set of physics for both games AND the
 * server (the server checks a finishing time against what that car could possibly do), so who wins comes down to the
 * car and how it's driven: the launch (revs in the green band when the light turns), and each gear change (near the
 * red line; too early and the engine bogs, at the limiter it stops pulling). An electric car has one gear: nothing to
 * shift, everything on the launch. Deterministic: the same inputs, the same run, on any phone.
 *
 * The figures are each car's own, made up but in the right place for what it is (a forty-year-old Bulle has 25 kW
 * and 750 kg; the Vento 900 kW and 1.4 t); a bike's mass includes its rider.
 */

export const RACE_METRES = 402;

export interface Perf {
  /** peak power, kW */
  kw: number;
  /** mass with the driver, kg */
  kg: number;
  /** how much of its weight it can put down as push (tyres, all-wheel drive): ~0.8 a scooter, 1.35 an AWD hypercar */
  grip: number;
  /** top speed, km/h */
  top: number;
  /** forward gears (1: electric) */
  gears: number;
  /** the red line, rpm (an electric motor: unused) */
  redline: number;
  ev?: boolean;
}

const P = (kw: number, kg: number, grip: number, top: number, gears: number, redline = 6800, ev = false): Perf => ({ kw, kg, grip, top, gears, redline, ...(ev ? { ev } : {}) });

export const PERF: Record<string, Perf> = {
  // saloons and the cheap ones
  'kodo-corsa': P(125, 1450, 0.95, 200, 6), 'pelleton-406': P(100, 1350, 0.9, 190, 5), 'cartwright-meridian': P(200, 1700, 0.95, 220, 6, 6200),
  'bayan-lan': P(150, 1700, 1, 180, 1, 0, true), 'lumen-s5': P(250, 1800, 1, 250, 8, 7000), 'pelleton-bulle': P(25, 750, 0.85, 115, 4, 5000),
  'sora-mira': P(50, 900, 0.9, 150, 5, 6500), 'sora-sunny': P(80, 1100, 0.9, 170, 5, 6500), 'volta-arc': P(360, 1850, 1.15, 250, 1, 0, true),
  // convertibles and coupés
  'kodo-kaze': P(135, 1050, 1, 210, 6, 7500), 'cartwright-stallion': P(340, 1750, 0.95, 250, 6, 6500), 'pelleton-riviera': P(300, 1500, 1.05, 270, 7, 7200),
  'lumen-c8': P(250, 1900, 1, 250, 8, 7000), 'kodo-celeste': P(280, 1600, 1.05, 260, 6, 7000), 'lumen-maestro': P(460, 1950, 1.15, 315, 9, 6500),
  // SUVs
  'kodo-highland': P(220, 2200, 1, 190, 8, 6000), 'pelleton-rive': P(150, 1700, 0.95, 200, 6, 6200), 'cartwright-prairie': P(300, 2500, 1, 200, 10, 6000),
  'bayan-shan': P(230, 2200, 1.1, 200, 1, 0, true), 'lumen-r7': P(390, 2400, 1.1, 250, 8, 6800), 'kodo-trekker': P(200, 2300, 0.95, 180, 6, 5800),
  'volta-ridge': P(630, 3100, 1.1, 210, 1, 0, true), 'aurelio-monte': P(480, 2200, 1.2, 305, 8, 7000),
  // supercars and hypercars
  'lumen-gt6': P(330, 1450, 1.2, 310, 7, 8000), 'volta-apex': P(1000, 1900, 1.35, 400, 1, 0, true), 'aurelio-furia': P(566, 1550, 1.25, 350, 7, 8500),
  'aurelio-vento': P(900, 1400, 1.3, 400, 7, 8500), 'aurelio-vento-spider': P(900, 1430, 1.3, 395, 7, 8500), 'kodo-raiden': P(420, 1700, 1.25, 315, 6, 7000),
  'cartwright-thunder': P(560, 1800, 1.05, 320, 6, 6800), 'bayan-feng': P(1400, 2100, 1.35, 410, 1, 0, true), 'lumen-vx': P(610, 1600, 1.2, 345, 7, 8500),
  // two wheels (with the rider)
  'kodo-swift': P(8, 195, 0.85, 105, 4, 9000), 'bayan-dian': P(4, 175, 0.85, 60, 1, 0, true), 'pelleton-ville': P(9, 225, 0.85, 110, 1, 8500),
  'cartwright-roadster': P(55, 325, 0.9, 190, 6, 8000), 'lumen-rs': P(150, 275, 0.8, 299, 6, 14000),
};

/** a car's figures (one we haven't listed: what its kind of car usually has; no kind given, a supercar's, the
 * quickest guess, which is what the server wants when it checks a time) */
export function perfOf(model: string, body: string = 'super'): Perf {
  const p = PERF[model];
  if (p) return p;
  return body === 'super' ? P(450, 1600, 1.2, 320, 7, 8000) : body === 'suv' ? P(220, 2100, 1, 200, 8, 6200) : body === 'bike' ? P(10, 200, 0.85, 110, 4, 9000) : P(120, 1400, 0.95, 195, 6);
}

/** one car on the strip */
export interface Run {
  /** metres from the line */
  d: number;
  /** m/s */
  v: number;
  gear: number;
  rpm: number;
  /** seconds since the green */
  t: number;
  /** a gear change under way: seconds left */
  shifting: number;
  /** after the launch: wheelspin (too many revs) or a bog (too few), seconds left, and the launch's verdict */
  spin: number;
  bog: number;
  launch: 'perfect' | 'good' | 'spin' | 'bog' | null;
  /** finished: the time, seconds */
  done: number | null;
}

export const newRun = (): Run => ({ d: 0, v: 0, gear: 0, rpm: 900, t: 0, shifting: 0, spin: 0, bog: 0, launch: null, done: null });

const G = 9.81, IDLE = 900;
/** the speed (m/s) at the red line in each gear: first takes a third of the top speed, top gear all of it */
export function gearTops(p: Perf): number[] {
  const top = p.top / 3.6;
  if (p.gears <= 1) return [top];
  return Array.from({ length: p.gears }, (_, i) => top * (0.34 + 0.66 * Math.pow(i / (p.gears - 1), 0.85)));
}
/** the share of peak power at this much of the red line: a petrol engine's curve, best near 90% */
const curve = (x: number) => Math.max(0.12, 1 - ((x - 0.9) / 0.78) ** 2);
/** the launch band (share of the red line): revs in it when the light goes green is a clean start */
export const LAUNCH = { lo: 0.5, hi: 0.78, perfectLo: 0.58, perfectHi: 0.7 };
/** when to change up: the shift light (share of the red line) */
export const SHIFT_AT = 0.93;

/**
 * Before the green: the clutch in, the gas revs the engine (up while held, falling back when not).
 * `gas` 0..1.
 */
export function revEngine(r: Run, p: Perf, gas: number, dt: number) {
  if (p.ev) { r.rpm = gas; return; } // (a motor: the pedal's position, read at the green)
  const target = IDLE + (p.redline - IDLE) * gas;
  r.rpm += (target - r.rpm) * Math.min(1, dt * (gas > r.rpm / p.redline ? 5 : 3));
}

/** The light turns green: how the start went, from the revs (or, electric, how much pedal) right now. */
export function launchRun(r: Run, p: Perf) {
  const x = p.ev ? r.rpm : r.rpm / p.redline;
  // (electric: full pedal is a clean start, the traction control catches it; none at all, a slow one)
  if (p.ev) { r.launch = x > 0.5 ? 'perfect' : 'bog'; r.bog = x > 0.5 ? 0 : 0.5; r.rpm = 0; return; }
  if (x >= LAUNCH.perfectLo && x <= LAUNCH.perfectHi) r.launch = 'perfect';
  else if (x >= LAUNCH.lo && x <= LAUNCH.hi) r.launch = 'good';
  else if (x > LAUNCH.hi) { r.launch = 'spin'; r.spin = 0.4 + (x - LAUNCH.hi) * 4; }
  else { r.launch = 'bog'; r.bog = 0.4 + (LAUNCH.lo - x) * 2; }
}

/** Ask for the next gear (false: already in top, or a change is under way). */
export function shiftRun(r: Run, p: Perf): boolean {
  if (p.ev || r.shifting > 0 || r.gear >= p.gears - 1 || r.done !== null) return false;
  r.gear++; r.shifting = p.kw / p.kg > 0.3 ? 0.18 : 0.3; // (a fast car's gearbox is quicker)
  return true;
}

/** One step down the strip (after the green). `gas` 0..1. */
export function stepRun(r: Run, p: Perf, gas: number, dt: number) {
  if (r.done !== null) return;
  const tops = gearTops(p), kw = p.kw * 1000, top = p.top / 3.6;
  // the air: the car's top speed is where its power meets the drag
  const cda = (2 * kw * 0.9) / (1.2 * top ** 3), drag = 0.6 * cda * r.v * r.v, roll = 0.015 * p.kg * G;
  let push = 0;
  if (p.ev) {
    // a motor: all its pull from standstill (up to what the tyres take) until its power runs out
    const most = kw / Math.max(top * 0.28, 1);
    push = Math.min(most, kw / Math.max(r.v, 0.5)) * gas;
    r.rpm = r.v / top;
  } else {
    const red = tops[r.gear];
    r.rpm = Math.max(IDLE, (p.redline * r.v) / red);
    // (pulling away: the clutch slips, so the engine sits on the revs it launched with until the gear catches up)
    if (r.gear === 0 && r.t < 1.2) r.rpm = Math.max(r.rpm, p.redline * 0.55);
    const x = r.rpm / p.redline;
    push = x >= 1 ? 0 : (kw * curve(x) / Math.max(r.v, 2.5)) * gas;
    if (r.shifting > 0) { push = 0; r.shifting = Math.max(0, r.shifting - dt); }
  }
  if (r.bog > 0) { push *= 0.45; r.bog = Math.max(0, r.bog - dt); }
  const tyres = p.grip * p.kg * G * (r.spin > 0 ? 0.55 : 1);
  if (r.spin > 0) r.spin = Math.max(0, r.spin - dt);
  push = Math.min(push, tyres);
  const a = (push - drag - (r.v > 0.05 ? roll : 0)) / p.kg;
  r.v = Math.max(0, r.v + a * dt);
  const was = r.d;
  r.d += r.v * dt;
  r.t += dt;
  if (r.d >= RACE_METRES) {
    // (the line crossed between two steps: the time when it was crossed)
    const over = (r.d - RACE_METRES) / Math.max(r.d - was, 1e-6);
    r.done = r.t - dt * over;
  }
}

/** The fastest this car can do the quarter mile: a perfect launch and every change at the shift light. */
export function bestTime(p: Perf): number {
  const r = newRun(), dt = 1 / 120;
  r.rpm = p.ev ? 1 : p.redline * (LAUNCH.perfectLo + LAUNCH.perfectHi) / 2;
  launchRun(r, p);
  for (let i = 0; i < 120 * 120 && r.done === null; i++) {
    if (!p.ev && r.rpm >= p.redline * SHIFT_AT) shiftRun(r, p);
    stepRun(r, p, 1, dt);
  }
  return r.done ?? 999;
}

/** (the server) could this car have finished in this time? A little slack for frame timing. */
export const possibleTime = (model: string, secs: number) => Number.isFinite(secs) && secs >= bestTime(perfOf(model)) * 0.985 && secs < 180;
